import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  getPortfolio,
  type AttentionItem,
  type Portfolio as PortfolioData,
  type PortfolioProject,
} from '../api.js';
import { PROJECT_STATUS_LABELS, formatMoney, typeLabel } from '../labels.js';
import { navigate } from '../router.js';
import { RegisterToolbar, matches, useRegisterView, type FilterDef } from './register-kit.js';

/**
 * The portfolio home (DB-01 / W1).
 *
 * `12` W1 puts one note under the wireframe, and the whole layout follows from
 * it:
 *
 *   > the attention list is **first**, above the financial snapshot. Owners open
 *   > this screen to find out what is broken, not to admire the totals.
 *
 * So: counts, then **what is wrong**, then the money, then the list of projects.
 * `13` times the demo — *"sees a red project, clicks the badge, reads three
 * drivers, opens the source record. Under 30 seconds"* — so every attention row
 * is a link straight to the project that owns the record.
 *
 * NEVER COLOUR ALONE
 * ------------------
 * `12` says it under the wireframe: **icon + colour + text label**. Every badge
 * here carries a symbol AND a word, so the screen survives a printout, a
 * projector and colour blindness.
 */

/**
 * THE SAME THREE WORDS AS THE PULSE, because it is the same measurement.
 *
 * This badge and the Health panel on a project both render the SIGNAL colour
 * the Pulse worked out. Until 2026-09-02 red read "Off track" here and
 * "Critical" there, so one project could be described two ways depending on
 * which screen you were standing on — the portfolio, or the project you clicked
 * through to from it. Nobody can tell that is one state under two names.
 *
 * "Critical" is the product's word for red: the Pulse uses it, and so does
 * material risk, which pairs it with "At risk" exactly as this does.
 *
 * Not "Needs attention", which is the section heading below and counts ITEMS
 * where this counts PROJECTS. Two identical labels on one screen is ambiguous
 * read aloud and worse skimmed — found by the screen test, which could not tell
 * them apart either. `12` W1's "DELAYED" is rejected for its own reason: it is
 * schedule-specific, and a project can be red on cost or quality.
 */
const HEALTH_WORDS: Readonly<Record<string, string>> = {
  green: 'On track',
  amber: 'At risk',
  red: 'Critical',
  unknown: 'Not measured',
};

const HEALTH_SYMBOLS: Readonly<Record<string, string>> = {
  green: '●',
  amber: '▲',
  red: '■',
  unknown: '○',
};

const SIGNAL_WORDS: Readonly<Record<string, string>> = {
  schedule: 'Schedule',
  approvals: 'Approvals',
  procurement: 'Procurement',
  site: 'Site',
  quality: 'Quality',
  commercial: 'Commercial',
};

const STAGE_WORDS: Readonly<Record<string, string>> = {
  award: 'Award',
  design: 'Design',
  approvals: 'Approvals',
  procurement: 'Procurement',
  site_execution: 'Site',
  testing_commissioning: 'Testing',
  snagging: 'Snagging',
  handover: 'Handover',
};

/** The PM filter's value for a project nobody is running yet. */
const UNASSIGNED = '__none__';

export function Portfolio(): JSX.Element {
  const [data, setData] = useState<PortfolioData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * `12` W1: [Search] [Health ▾] [Type ▾] [PM ▾].
   *
   * IN THE BROWSER, NOT ON THE SERVER, and deliberately. The whole list is
   * already loaded — a portfolio is tens of projects, not thousands, there is
   * no pagination, and this page computes a Pulse per project. Re-fetching all
   * of that on every keystroke would be absurd, and filtering here answers
   * instantly, which is most of what makes a filter worth using.
   *
   * The day a company has two hundred projects this moves to the server — and
   * that is the same day the Pulse recompute has to move to `health_snapshot`.
   * Both together, or neither.
   *
   * It is the shared `RegisterToolbar` now, so this screen counts and clears
   * the way every register does. The sort is deliberately the identity: the
   * server hands back the attention list first (`06`), and re-ordering it in
   * the browser would throw away the one thing the portfolio is FOR.
   */
  const view = useRegisterView('server');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setData(await getPortfolio());
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load the portfolio.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data) {
    return (
      <section>
        <h2>Portfolio</h2>
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Working out where everything stands…</p>
        )}
      </section>
    );
  }

  const health = view.chosen.health ?? '';
  const type = view.chosen.type ?? '';
  const pm = view.chosen.pm ?? '';

  const shown = data.projects.filter((project) => {
    if (health && project.health !== health) return false;
    if (type && project.project_type !== type) return false;
    if (pm && (pm === UNASSIGNED ? project.pm_name !== null : project.pm_name !== pm)) return false;
    // Code, name and client together. Somebody looking for a job has one of
    // the three in their head and should not have to know which box it lives
    // in — "Bluewater" and "PR098" both have to find the same row.
    return matches(
      [project.code, project.name, project.client_name ?? ''].join(' '),
      view.query,
    );
  });

  return (
    <section>
      <div className="section-head">
        <h2>Portfolio</h2>
        <button type="button" className="link-button" disabled={busy} onClick={() => void load()}>
          Work it out again
        </button>
      </div>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {data.projects.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No projects yet.</strong>
          </p>
          <p>Once there are some, this is where you will see which need you.</p>
        </div>
      ) : (
        <>
          <ul className="portfolio-counts">
            {(['red', 'amber', 'green', 'unknown'] as const).map((key) => (
              <li key={key} className={`portfolio-count pulse-${key}`}>
                <span className="portfolio-number">
                  <span className="pulse-symbol" aria-hidden="true">
                    {HEALTH_SYMBOLS[key]}
                  </span>{' '}
                  {data.counts[key] ?? 0}
                </span>
                <span className="portfolio-word">{HEALTH_WORDS[key]}</span>
              </li>
            ))}
          </ul>

          {/* FIRST, above the money. `12` W1. */}
          <Attention items={data.needs_attention} />

          {data.financial && <Financial snapshot={data.financial} />}

          <h3 className="sub-head">Projects</h3>

          {/*
            A dropdown offering "Retail" to a company that only does offices
            produces empty results and teaches people the filter is broken, so
            each one is built from what is actually there and dropped when it
            would have a single answer. Search always earns its place: it
            matches on words rather than on categories.
          */}
          <RegisterToolbar
            view={view}
            searchLabel="Search"
            searchPlaceholder="Code, name or client"
            shown={shown.length}
            total={data.projects.length}
            noun={['project', 'projects']}
            filters={filtersFor(data.projects)}
          />

          {shown.length === 0 ? (
            // Distinguishable from an empty portfolio, which is the mistake
            // this codebase has made nine times on other screens. "No projects
            // yet" to somebody who has twenty and mistyped a filter would send
            // them looking for a fault that is not there.
            <div className="empty">
              <p>
                {/*
                  An empty PORTFOLIO is handled above, before the filters are
                  even drawn. Anything empty down here is the filters, and it
                  has to say so.
                */}
                <strong>Nothing matches those filters.</strong>
              </p>
              {data.projects.length > 0 && (
                <p>
                  You have {data.projects.length}{' '}
                  {data.projects.length === 1 ? 'project' : 'projects'} — none of them match.
                </p>
              )}
            </div>
          ) : (
            <table className="grid">
              <caption className="visually-hidden">
                Every project, with its health and where it has got to
              </caption>
              <thead>
                <tr>
                  <th scope="col">Project</th>
                  <th scope="col">Client</th>
                  <th scope="col">Stage</th>
                  <th scope="col">Done</th>
                  <th scope="col">Manager</th>
                  <th scope="col">Health</th>
                  {data.financial && <th scope="col">Contract</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((project) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    showsMoney={data.financial !== undefined}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function Attention({ items }: { readonly items: readonly AttentionItem[] }): JSX.Element {
  return (
    <>
      <h3 className="sub-head">Needs attention{items.length > 0 ? ` (${items.length})` : ''}</h3>

      {items.length === 0 ? (
        // Said plainly rather than shown as an absence. An empty space reads as
        // "not loaded" and this is the one screen where that matters.
        <p className="hint">
          Nothing is overdue, blocked or unapproved across your projects right now.
        </p>
      ) : (
        <ul className="attention-list">
          {items.map((item) => (
            <li
              key={`${item.project_id}-${item.entity_id ?? item.reference}`}
              className={`attention-item pulse-${item.severity}`}
            >
              <a
                href={`/projects/${encodeURIComponent(item.project_id)}`}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                  event.preventDefault();
                  navigate(`/projects/${encodeURIComponent(item.project_id)}`);
                }}
              >
                <span className="pulse-symbol" aria-hidden="true">
                  {HEALTH_SYMBOLS[item.severity]}
                </span>{' '}
                <strong>{item.project_code}</strong>{' '}
                <span className="attention-ref">{item.reference}</span>{' '}
                <span className="attention-why">{item.why}</span>
                {/* The word matters as much as the colour. */}
                <span className="visually-hidden">
                  {' '}
                  — {HEALTH_WORDS[item.severity]}, {SIGNAL_WORDS[item.signal] ?? item.signal}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function Financial({
  snapshot,
}: {
  readonly snapshot: NonNullable<PortfolioData['financial']>;
}): JSX.Element {
  const exposed = Number(snapshot.unapproved_exposure) > 0;
  const overdue = Number(snapshot.overdue) > 0;

  return (
    <>
      <h3 className="sub-head">Money, across every project</h3>
      <table className="grid">
        <caption className="visually-hidden">The financial position across the portfolio</caption>
        <tbody>
          <tr>
            <th scope="row">Contract</th>
            <td>{formatMoney(snapshot.contract)}</td>
          </tr>
          <tr>
            <th scope="row">Approved variations</th>
            <td>{formatMoney(snapshot.approved_variations)}</td>
          </tr>
          <tr>
            <th scope="row">Revised contract</th>
            <td>{formatMoney(snapshot.revised_contract)}</td>
          </tr>
          <tr>
            <th scope="row">Forecast cost</th>
            <td>{formatMoney(snapshot.forecast_cost)}</td>
          </tr>
          <tr>
            <th scope="row">Invoiced</th>
            <td>{formatMoney(snapshot.billed)}</td>
          </tr>
          <tr>
            <th scope="row">Received</th>
            <td>{formatMoney(snapshot.collected)}</td>
          </tr>
          <tr className={overdue ? 'vo-exposed' : undefined}>
            <th scope="row">
              Overdue
              {overdue && <span className="task-overdue">{' · '}Money the client owes past its date</span>}
            </th>
            <td>{formatMoney(snapshot.overdue)}</td>
          </tr>
          {/* The line a director looks for, and the reason the register exists. */}
          <tr className={exposed ? 'vo-exposed' : undefined}>
            <th scope="row">
              Unapproved exposure
              {exposed && (
                <span className="task-overdue">{' · '}Work instructed that nobody agreed to pay for</span>
              )}
            </th>
            <td>{formatMoney(snapshot.unapproved_exposure)}</td>
          </tr>
        </tbody>
      </table>

      {snapshot.projects_over_budget > 0 && (
        <p className="hint">
          <strong>
            {snapshot.projects_over_budget}{' '}
            {snapshot.projects_over_budget === 1 ? 'project has' : 'projects have'} a budget line
            heading over.
          </strong>{' '}
          A total can look comfortable while one trade is badly over.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * `12` W1's filter row.
 *
 * The options come from the projects THEMSELVES, not from a fixed list of every
 * type and role the system knows about. A dropdown offering "Retail" to a
 * company that only does offices is a dropdown that produces empty results and
 * teaches people the filter is broken.
 */
/**
 * The filters this portfolio can usefully offer, built from what is in it.
 *
 * A dropdown offering "Retail" to a company that only does offices produces
 * empty results and teaches people the filter is broken — so a filter with a
 * single answer is not offered at all.
 */
function filtersFor(projects: readonly PortfolioProject[]): readonly FilterDef[] {
  const filters: FilterDef[] = [];

  // Worst first. Somebody opening a health filter is looking for trouble.
  const healths = ['red', 'amber', 'green', 'unknown'].filter((key) =>
    projects.some((p) => p.health === key),
  );
  if (healths.length > 1) {
    filters.push({
      key: 'health',
      label: 'Health',
      options: healths.map((key) => ({ value: key, label: HEALTH_WORDS[key] ?? key })),
    });
  }

  const types = [...new Set(projects.map((p) => p.project_type))].sort();
  if (types.length > 1) {
    filters.push({
      key: 'type',
      label: 'Type',
      options: types.map((key) => ({ value: key, label: typeLabel(key) })),
    });
  }

  const managers = [...new Set(projects.map((p) => p.pm_name).filter(Boolean))].sort() as string[];
  const anyUnassigned = projects.some((p) => p.pm_name === null);
  if (managers.length > 1 || (managers.length === 1 && anyUnassigned)) {
    filters.push({
      key: 'pm',
      label: 'Manager',
      options: [
        ...managers.map((name) => ({ value: name, label: name })),
        // Offered, not hidden. A project with nobody running it is exactly
        // what somebody filtering by manager is trying to find.
        ...(anyUnassigned ? [{ value: UNASSIGNED, label: 'Nobody assigned' }] : []),
      ],
    });
  }

  return filters;
}

// ---------------------------------------------------------------------------

function ProjectRow({
  project,
  showsMoney,
}: {
  readonly project: PortfolioProject;
  readonly showsMoney: boolean;
}): JSX.Element {
  return (
    <tr className={project.health === 'red' ? 'vo-exposed' : undefined}>
      <th scope="row">
        <a
          href={`/projects/${encodeURIComponent(project.id)}`}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            navigate(`/projects/${encodeURIComponent(project.id)}`);
          }}
        >
          <span className="mono">{project.code}</span> {project.name}
        </a>
        {/*
          The separator lives INSIDE the span, which is the house pattern and
          exists for a reason: JSX drops the whitespace between two elements on
          separate lines, so without it this read "Business Bay Office
          Fit-OutOffice · Active".
        */}
        <span className="tree-code">
          {' · '}
          {typeLabel(project.project_type)} ·{' '}
          {PROJECT_STATUS_LABELS[project.status] ?? project.status}
        </span>
      </th>
      <td data-label="Client">{project.client_name ?? '—'}</td>
      <td data-label="Stage">
        {project.current_stage
          ? (STAGE_WORDS[project.current_stage] ?? project.current_stage)
          : '—'}
      </td>
      {/*
        Blank, not 0%. A project whose activities carry no programme is
        unmeasured, and a column of zeros would read as "nothing has been done"
        across half a healthy portfolio.
      */}
      <td className="mono" data-label="Done">
        {project.progress_percent === null ? (
          <span className="tree-code">Not planned</span>
        ) : (
          `${project.progress_percent}%`
        )}
      </td>
      <td data-label="Manager">
        {project.pm_name ?? <span className="tree-code">Nobody assigned</span>}
      </td>
      <td data-label="Health">
        <span className="pulse-symbol" aria-hidden="true">
          {HEALTH_SYMBOLS[project.health]}
        </span>{' '}
        {HEALTH_WORDS[project.health]}
        {project.driven_by && (
          <span className="tree-code">
            {' · '}
            {SIGNAL_WORDS[project.driven_by] ?? project.driven_by}
          </span>
        )}
        {/*
          A green badge on a project nobody has entered anything into would read
          as "all fine". The Pulse screen says "which is not the same as being
          fine" underneath; a row has no space for that sentence, so it says how
          much was measured instead.
        */}
        {project.measured < 6 && (
          <span className="snag-note">from {project.measured} of 6 signals</span>
        )}
      </td>
      {showsMoney && <td data-label="Contract">{formatMoney(project.contract_value ?? null)}</td>}
    </tr>
  );
}
